import { computed, watch } from 'vue';
import { SelectionPrefix } from '@sembo-private/constraint-builder';

import type {
  PortStaticInfo,
  Dictionary,
  TransportVehicle,
} from '@libs/trip-planner-interfaces';
import {
  AutoCompleteSearchType,
  CategoryType,
  IngredientType,
  TransportVehicleType,
} from '@libs/trip-planner-interfaces';
import { assertValue } from '@libs/utils';

import type { RecipeState } from '../state/recipeState';
import type { TripState } from '../state/tripState';
import type TripPlannerModel from '../tripPlannerModel/TripPlannerModel';
import { isHotelIngredient } from '../recipe/ingredients';
import type { HotelIngredient } from '../recipe/ingredients';
import type { OwnStayIngredient } from '../recipe/ingredients/ownStayIngredient';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function useRecipeViewModel(
  recipeState: RecipeState,
  tripState: TripState,
  model: TripPlannerModel
) {
  const recipeIatas = computed(() =>
    recipeState.flightOnlyRecipe.value.flatMap((flight) => {
      assertValue(flight.fromPort, 'RecipeViewModel > recipeIatas > fromPort');
      assertValue(flight.toPort, 'RecipeViewModel > recipeIatas > toPort');
      return [flight.fromPort, flight.toPort];
    })
  );
  watch(
    recipeIatas,
    (iatas) => {
      tripState.isSearchingFlightsStaticInfo.value = true;

      model.searchAirportStaticInfo(iatas).subscribe({
        next: (ports: Dictionary<PortStaticInfo>) => {
          tripState.insertPortStaticInfo(ports, IngredientType.Flight);
          tripState.setFlightStaticInfoToRecipe();
        },
        complete: () => {
          tripState.isSearchingFlightsStaticInfo.value = false;
        },
      });
    },
    { immediate: true }
  );

  const recipeSeaports = computed(() =>
    recipeState.ferryOnlyRecipe.value.flatMap((ferry) => {
      assertValue(
        ferry.fromPort,
        'RecipeViewModel > recipeSeaports > fromPort'
      );
      assertValue(ferry.toPort, 'RecipeViewModel > recipeSeaports > toPort');
      return [ferry.fromPort, ferry.toPort];
    })
  );
  watch(
    recipeSeaports,
    (seaports) => {
      tripState.isSearchingFerriesStaticInfo.value = true;

      model.searchSeaportStaticInfo(seaports).subscribe({
        next: (ports: Dictionary<PortStaticInfo>) => {
          tripState.insertPortStaticInfo(ports, IngredientType.Ferry);
          tripState.setFerryStaticInfoToRecipe();
        },
        complete: () => {
          tripState.isSearchingFerriesStaticInfo.value = false;
        },
        error: () => (tripState.isSearchingFerriesStaticInfo.value = false),
      });
    },
    { immediate: true }
  );

  model.getFerryVehicleTypes().subscribe(
    (response) =>
      (tripState.ferryVehicleTypes.value = response.reduce(
        (acc: TransportVehicle[], vehicle: TransportVehicle) => {
          if (vehicle.id === TransportVehicleType.None) {
            acc.unshift(vehicle);
          } else {
            acc.push(vehicle);
          }

          return acc;
        },
        []
      ))
  );

  const recipeStaySearchTexts = computed(() =>
    recipeState.stayOnlyRecipe.value.map(({ searchText, ingredientId }) => ({
      searchText,
      ingredientId,
    }))
  );
  watch(
    recipeStaySearchTexts,
    (staySearchTexts) => {
      staySearchTexts.forEach(({ ingredientId }) => {
        const stayIngredient = recipeState.getStayIngredient(ingredientId);
        if (!stayIngredient) {
          return;
        }
        updateDisplayText(stayIngredient);

        if (isHotelIngredient(stayIngredient)) {
          updateHotelPosition(stayIngredient);

          updateNearbyPolygon(stayIngredient);
        }
      });
    },
    { immediate: true }
  );

  /**
   * Checks first if hotel ingredient searchText is a hotel name.
   * If it is, updates the position of that ingredient.
   * It is needed for accurately suggesting airports.
   *
   * @param {HotelIngredient} hotelIngredient - The hotel ingredient to update.
   */
  function updateHotelPosition(hotelIngredient: HotelIngredient): void {
    hotelIngredient.positionFromSearchText = undefined;

    model
      .searchAutoCompleteSuggestions({
        searchText: hotelIngredient.searchText,
        searchType: AutoCompleteSearchType.Destination,
      })
      .subscribe((suggestionResult) => {
        if (suggestionResult[0]) {
          const isHotelNameSearchText =
            suggestionResult[0].type === CategoryType.Hotel;

          if (isHotelNameSearchText) {
            model
              .searchHotelPosition(hotelIngredient.searchText)
              .subscribe(
                (hotelPositionResult) =>
                  (hotelIngredient.positionFromSearchText = hotelPositionResult)
              );
          }
        }
      });
  }

  function updateDisplayText(
    stayIngredient: HotelIngredient | OwnStayIngredient
  ): void {
    const SELECTION_PREFIXES = Object.values(SelectionPrefix).filter(
      (prefix) => prefix !== SelectionPrefix.FullText
    );

    const hasSerializedConstraintSearchText = SELECTION_PREFIXES.some(
      (prefix) => stayIngredient.searchText.startsWith(prefix)
    );

    if (
      hasSerializedConstraintSearchText &&
      (!stayIngredient.displayText ||
        stayIngredient.displayText === stayIngredient.searchText)
    ) {
      if (stayIngredient.searchText.startsWith(SelectionPrefix.HotelCode)) {
        model
          .searchHotelName(stayIngredient.searchText)
          .subscribe((response) => {
            stayIngredient.displayText = response;
          });
      } else {
        model
          .searchHotelMainPolygonName(stayIngredient.searchText)
          .subscribe((response) => {
            stayIngredient.displayText = response;
          });
      }
    }
  }

  function updateNearbyPolygon(hotelIngredient: HotelIngredient): void {
    if (hotelIngredient.searchText.startsWith(SelectionPrefix.HotelCode)) {
      model
        .searchHotelMainPolygonName(hotelIngredient.searchText)
        .subscribe(
          (response) => (hotelIngredient.nearbyPolygonText = response)
        );
    }
  }
}
